import { ClassNames } from '@emotion/react'
import React, { Fragment, type ReactElement, type ReactNode } from 'react'

import type { ColorToken } from '../themes'
import { InternalTextButton } from './TextButton'
import { getColorValue, useTheme } from './ThemeProvider'

export type RichTextAttributeStyleType =
  | 'bold'
  | 'italic'
  | 'underline'
  | 'highlight'
  | 'none'

export type RichTextAttributeType = {
  highlightColor?: ColorToken
  length: number
  link?: string
  location: number
  style: RichTextAttributeStyleType | null
}

export type RichTextType = {
  attributes: null | ReadonlyArray<RichTextAttributeType>
  text: string
}

export type RichCharacter = {
  character: string
  highlightColor?: ColorToken
  styles: Array<RichTextAttributeStyleType>
  url: null | string
}

export type RenderStyle = (renderProps: {
  children: ReactNode
  highlightColor?: ColorToken
  style: RichTextAttributeStyleType
}) => ReactElement | null

// TODO (@auster.chen): Update to remove className and switch to using overrideStyles.
type Props = {
  className?: string
  renderLink?: (renderProps: {
    children: ReactNode
    url: string
  }) => ReactElement | null
  renderStyle?: RenderStyle
  value: RichTextType
}

/**
 * This component renders a Robinhood RichText object into the appropriate styled text.
 * It should only be used to render text provided by an API, and even for that use case we'd recommend you use Markdown (and the `Markdown` component) instead.
 */
export function RichText({
  value,
  renderLink = defaultRenderLink,
  renderStyle = DefaultRenderStyle,
  className,
}: Props): ReactElement | null {
  let content: ReactNode

  if (!value.attributes || !value.attributes.length) {
    content = value.text
  } else {
    const richCharacters = getRichCharacters(value)

    // Split into contiguous strings of characters with the same url (or no url). This
    // ensures that we always format links as a single tag
    const urlGroups = getUrlGroups(richCharacters)

    // render each block with the correct styles
    content = urlGroups.map((urlGroup, index) => {
      const url = urlGroup[0].url
      const text = renderStyles(urlGroup, renderStyle)

      return (
        <Fragment key={index}>
          {url ? renderLink({ children: text, url }) : text}
        </Fragment>
      )
    })
  }

  return (
    <ClassNames>
      {({ css, cx }) => (
        <span className={cx(className, css({ whiteSpace: 'pre-wrap' }))}>
          {content}
        </span>
      )}
    </ClassNames>
  )
}

function renderStyles(
  richCharacters: ReadonlyArray<RichCharacter>,
  renderStyle: RenderStyle
): ReactElement[] | string {
  // base case is no style — none of the characters have any style applied
  if (richCharacters.every(rc => rc.styles.length === 0)) {
    return richCharacters.map(rc => rc.character).join('')
  }

  const nodes: Array<ReactNode> = []
  for (let i = 0; i < richCharacters.length; i += 1) {
    const tagStyle = richCharacters[i].styles[0] || null
    const tagHighlightColor = richCharacters[i].highlightColor

    // push all of the characters that should go into this tag into a
    // single array
    const tagCharacters = [richCharacters[i]]
    for (let j = i + 1; j < richCharacters.length; j += 1) {
      const potentialChar = richCharacters[j]
      if (tagStyle === null && potentialChar.styles.length === 0) {
        tagCharacters.push(potentialChar)
      } else if (potentialChar.styles.includes(tagStyle)) {
        tagCharacters.push(potentialChar)
      } else {
        break
      }
    }

    // wrap all of the characters that should go into this tag in the
    // correct style and add the wrapped characters to our final list
    // of nodes

    if (tagStyle === null) {
      // this tag meets our base case: run it through for the final
      // recursive pass
      nodes.push(renderStyles(tagCharacters, renderStyle))
    } else {
      // strip this tag's characters of the current tag's style before
      // making the recursive step to add any remaining styles
      const tagCharactersWithoutTagStyle = tagCharacters.map(rc => ({
        ...rc,
        styles: rc.styles.filter(s => s !== tagStyle),
      }))
      const tagText = renderStyles(tagCharactersWithoutTagStyle, renderStyle)
      nodes.push(
        renderStyle({
          children: tagText,
          highlightColor: tagHighlightColor,
          style: tagStyle,
        })
      )
    }

    // shift the read index to the spot after the characters we've pushed
    // into this tag, then subtract 1 since the for loop's incrementer will
    // add it back
    i += tagCharacters.length - 1
  }

  return nodes.map((node, i) => <Fragment key={i}>{node}</Fragment>)
}

function getRichCharacters(value: RichTextType): ReadonlyArray<RichCharacter> {
  const arr: Array<RichCharacter> = value.text.split('').map(c => ({
    character: c,
    highlight_color: undefined,
    styles: [],
    url: null,
  }))

  if (value.attributes) {
    value.attributes.forEach(attr => {
      for (let i = attr.location; i - attr.location < attr.length; i += 1) {
        const char = arr[i]
        if (attr.style !== null) {
          char.styles.push(attr.style)
        }
        if (attr.link !== undefined) {
          char.url = attr.link
        }
        if (attr.highlightColor !== undefined) {
          char.highlightColor = attr.highlightColor
        }
      }
    })
  }

  return arr
}

/**
 * Splits `chars` into chunks where every element in the chunk has
 * the same url.
 */
function getUrlGroups(
  chars: ReadonlyArray<RichCharacter>
): ReadonlyArray<ReadonlyArray<RichCharacter>> {
  const groups: Array<Array<RichCharacter>> = []

  if (!chars.length) {
    return groups
  }

  let currUrl = chars[0].url
  let currGroup = [chars[0]]

  for (let i = 1; i < chars.length; i += 1) {
    const nextItem = chars[i]
    const nextValue = nextItem.url
    if (nextValue === currUrl) {
      currGroup.push(nextItem)
    } else {
      groups.push(currGroup)
      currGroup = [nextItem]
      currUrl = nextValue
    }
  }
  groups.push(currGroup)
  return groups
}

function defaultRenderLink({
  children,
  url,
}: {
  children: ReactNode
  url: string
}): ReactElement {
  return (
    <InternalTextButton
      href={url}
      isLegacyStyle
      label={children}
      type="anchor"
    />
  )
}

// TODO (@auster.chen): update to use `Type` component later.
const DefaultRenderStyle: RenderStyle = ({
  children,
  highlightColor,
  style,
}) => {
  const theme = useTheme()
  switch (style) {
    case 'bold':
      return <strong>{children}</strong>
    case 'italic':
      return <em>{children}</em>
    case 'underline':
      return <u>{children}</u>
    case 'highlight':
      return highlightColor ? (
        <span
          css={{
            color: getColorValue(highlightColor, theme),
          }}
        >
          {children}
        </span>
      ) : (
        <>{children}</>
      )

    default:
      return <>{children}</>
  }
}
